import { useState, useEffect, useCallback } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axiosInstance from "../../../../components/axiosInstance";
import { Pagination } from "@mui/material";
import { API_URLS } from "../../../../apiUrl";

interface DailyDeposit {
  date: string;
  remarks: string;
  cashier_name: string;
  total_amount: number;
}

interface DailyMapping {
  date: string;
  collector_name: string;
  total_amount: number;
  receipt_number: string;
}

interface SummaryData {
  users: {
    total_field_collectors: number;
    total_cashiers: number;
    total_counter_collectors: number;
  };
  water_connections: {
    active: number;
    inactive: number;
    opening_due_paid: number;
    opening_due_not_paid: number;
  };
  daily_deposits_by_cashier: DailyDeposit[];
  daily_mappings_by_collector: DailyMapping[];
}

const EMPTY_SUMMARY: SummaryData = {
  users: { total_field_collectors: 0, total_cashiers: 0, total_counter_collectors: 0 },
  water_connections: { active: 0, inactive: 0, opening_due_paid: 0, opening_due_not_paid: 0 },
  daily_deposits_by_cashier: [],
  daily_mappings_by_collector: [],
};

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

const AdminScreen = () => {
  const [summary, setSummary] = useState<SummaryData>(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(true);
  const [pageDeposit, setPageDeposit] = useState(1);
  const [pageMapping, setPageMapping] = useState(1);

  const rowsPerPage = 10;

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(API_URLS.summary);
      setSummary(res.data?.data || EMPTY_SUMMARY);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to load dashboard data";
      toast.error(msg, { position: "top-right", autoClose: 4000 });
      setSummary(EMPTY_SUMMARY);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const totalDeposit = summary.daily_deposits_by_cashier.reduce((s, d) => s + d.total_amount, 0);
  const totalMapped = summary.daily_mappings_by_collector.reduce((s, d) => s + d.total_amount, 0);
  const pendingBalance = totalDeposit - totalMapped;
  const totalArrear = summary.water_connections.opening_due_not_paid;

  const paginatedDeposits = summary.daily_deposits_by_cashier.slice(
  (pageDeposit - 1) * rowsPerPage,
  pageDeposit * rowsPerPage
);

const paginatedMappings = summary.daily_mappings_by_collector.slice(
  (pageMapping - 1) * rowsPerPage,
  pageMapping * rowsPerPage
);
const [remarksPopup, setRemarksPopup] = useState<string | null>(null);

const truncateRemarks = (text: string, wordLimit = 10) => {
  if (!text) return "";
  const words = text.split(" ");
  if (words.length <= wordLimit) return text;
  return words.slice(0, wordLimit).join(" ") + "...";
};
  return (
    <div className="bg-gray-100 min-h-screen mt-5">

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10">
        <div className="bg-white shadow-lg rounded-xl p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-600">Today's Deposits</h3>
          {loading
            ? <div className="h-9 w-32 bg-gray-200 rounded-lg animate-pulse mt-2" />
            : <p className="text-3xl font-bold text-green-600 mt-2">₹ {totalDeposit.toLocaleString()}</p>}
        </div>
        <div className="bg-white shadow-lg rounded-xl p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-600">Total Mapped</h3>
          {loading
            ? <div className="h-9 w-32 bg-gray-200 rounded-lg animate-pulse mt-2" />
            : <p className="text-3xl font-bold text-blue-600 mt-2">₹ {Math.round(totalMapped).toLocaleString()}</p>}
        </div>
        <div className="bg-white shadow-lg rounded-xl p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-600">Pending Balance</h3>
          {loading
            ? <div className="h-9 w-32 bg-gray-200 rounded-lg animate-pulse mt-2" />
            : <p className="text-3xl font-bold text-orange-600 mt-2">₹ {Math.round(pendingBalance).toLocaleString()}</p>}
        </div>
        <div className="bg-white shadow-lg rounded-xl p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-600">Opening Due Pending</h3>
          {loading
            ? <div className="h-9 w-16 bg-gray-200 rounded-lg animate-pulse mt-2" />
            : <p className="text-3xl font-bold text-red-600 mt-2">{totalArrear}</p>}
        </div>
      </div>

      {/* Daily Collection by Cashier */}
      <div className="bg-white shadow-lg rounded-xl p-6 mb-10 border border-gray-200">
        <h2 className="text-xl font-bold mb-6 text-gray-800">Daily Collection Amount (Deposited by Cashier)</h2>
           <div className="overflow-x-auto max-h-[350px] overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cashier</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount (₹)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remarks</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                Array.from({length: 3}).map((_, i) => (
                  <tr key={i}>
                    {Array.from({length: 4}).map((_, j) => (
                      <td key={j} className="px-6 py-4"><div className="h-4 bg-gray-200 rounded animate-pulse" style={{width: j===2?"60%":"80%"}} /></td>
                    ))}
                  </tr>
                ))
              ) : paginatedDeposits.length === 0 ? (
                <tr><td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500">No deposits recorded</td></tr>
              ) : (
                paginatedDeposits.map((dep, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(dep.date)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{dep.cashier_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">₹ {dep.total_amount.toLocaleString()}</td>
<td className="px-6 py-4 text-sm text-gray-500">
  {dep.remarks ? (
    dep.remarks.split(" ").length > 10 ? (
      <div className="flex items-center gap-2">
        <span>{truncateRemarks(dep.remarks)}</span>
        <button
          onClick={() => setRemarksPopup(dep.remarks)}
          className="text-blue-500 hover:text-blue-700"
        >
          <i className="ri-information-fill text-lg"></i>
        </button>
      </div>
    ) : (
      dep.remarks
    )
  ) : "—"}
</td>                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex justify-center mt-4">
  <Pagination
    count={Math.ceil(summary.daily_deposits_by_cashier.length / rowsPerPage)}
    page={pageDeposit}
    onChange={(_, value) => setPageDeposit(value)}
    color="primary"
  />
</div>
      </div>

      {/* Daily Mappings by Collector */}
      <div className="bg-white shadow-lg rounded-xl p-6 mb-10 border border-gray-200">
        <h2 className="text-xl font-bold mb-6 text-gray-800">Daily Mappings by Collector</h2>
        <div className="overflow-x-auto max-h-[350px] overflow-y-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Collector</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount (₹)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Receipt No</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                Array.from({length: 3}).map((_, i) => (
                  <tr key={i}>
                    {Array.from({length: 4}).map((_, j) => (
                      <td key={j} className="px-6 py-4"><div className="h-4 bg-gray-200 rounded animate-pulse" style={{width: j===2?"60%":"80%"}} /></td>
                    ))}
                  </tr>
                ))
              ) : paginatedMappings.length === 0 ? (
                <tr><td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500">No mappings recorded</td></tr>
              ) : (
                paginatedMappings.map((m, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(m.date)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{m.collector_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">₹ {Math.round(m.total_amount).toLocaleString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                      {m.receipt_number && m.receipt_number !== "NA" ? m.receipt_number : "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex justify-center mt-4">
  <Pagination
    count={Math.ceil(summary.daily_mappings_by_collector.length / rowsPerPage)}
    page={pageMapping}
    onChange={(_, value) => setPageMapping(value)}
    color="primary"
  />
</div>
      </div>

      {/* Connection Status */}
      <div className="bg-white shadow-lg rounded-xl p-6 border border-gray-200">
        <h2 className="text-xl font-bold mb-6 text-gray-800">Connection Payment Status</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-green-50 rounded-lg p-4 text-center">
            <p className="text-xs text-gray-500 mb-1">Active Connections</p>
            {loading ? <div className="h-8 w-12 bg-gray-200 rounded-lg animate-pulse mx-auto mt-1" /> : <p className="text-2xl font-bold text-green-600">{summary.water_connections.active}</p>}
          </div>
          <div className="bg-red-50 rounded-lg p-4 text-center">
            <p className="text-xs text-gray-500 mb-1">Inactive</p>
            {loading ? <div className="h-8 w-12 bg-gray-200 rounded-lg animate-pulse mx-auto mt-1" /> : <p className="text-2xl font-bold text-red-500">{summary.water_connections.inactive}</p>}
          </div>
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <p className="text-xs text-gray-500 mb-1">Opening Due Paid</p>
            {loading ? <div className="h-8 w-12 bg-gray-200 rounded-lg animate-pulse mx-auto mt-1" /> : <p className="text-2xl font-bold text-blue-600">{summary.water_connections.opening_due_paid}</p>}
          </div>
          <div className="bg-orange-50 rounded-lg p-4 text-center">
            <p className="text-xs text-gray-500 mb-1">Opening Due Pending</p>
            {loading ? <div className="h-8 w-12 bg-gray-200 rounded-lg animate-pulse mx-auto mt-1" /> : <p className="text-2xl font-bold text-orange-600">{summary.water_connections.opening_due_not_paid}</p>}
          </div>
        </div>
      </div>

      {remarksPopup && (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg shadow-lg w-[400px] max-w-[90%] p-6 relative">
      <h2 className="text-lg font-semibold mb-3">Remarks</h2>
      <p className="text-gray-600 text-sm leading-relaxed">{remarksPopup}</p>
      <button
        onClick={() => setRemarksPopup(null)}
        className="absolute top-3 right-3 text-gray-400 hover:text-red-500"
      >
        <i className="ri-close-line text-xl"></i>
      </button>
    </div>
  </div>
)}

      <ToastContainer />
    </div>
  );
};

export default AdminScreen;